import { useState } from 'react';
import {
  Button,
  Label,
  Checkbox,
  SegmentedControl,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useUsageExport, type ExportMetric, type ExportView } from '@/api/manticore';
import { DateRangePicker } from '@/components/date-range-picker/DateRangePicker';
import styles from './ExportUsageDialog.module.css';

type PeriodMode = 'month' | 'custom';

const METRICS: { id: ExportMetric; label: string }[] = [
  { id: 'requests', label: 'Requests' },
  { id: 'volume', label: 'Volume' },
  { id: 'bandwidth', label: 'Bandwidth' },
];
const VIEWS: { id: ExportView; label: string; desc: string }[] = [
  { id: 'domain_day', label: 'Domain × day', desc: 'One row per domain per day — the full detail.' },
  { id: 'day', label: 'By day', desc: 'Daily totals across all domains.' },
  { id: 'domain', label: 'By domain', desc: 'Period totals per domain.' },
  { id: 'geo', label: 'By geo', desc: 'Daily VN / China / Global split (volume + requests only).' },
];

// Geo is opt-in: many customers have no geo data, so it would add an empty sheet
// by default. Everything else is on.
const DEFAULT_VIEWS: Record<ExportView, boolean> = {
  domain_day: true,
  day: true,
  domain: true,
  geo: false,
};

/** All-checked default for a checkbox set keyed by id. */
function allChecked<T extends string>(items: { id: T }[]): Record<T, boolean> {
  return Object.fromEntries(items.map((i) => [i.id, true])) as Record<T, boolean>;
}
const picked = <T extends string>(state: Record<T, boolean>): T[] =>
  (Object.keys(state) as T[]).filter((k) => state[k]);

function errorMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
}

/**
 * Export the Business Usage data as a multi-sheet .xlsx. The customer chooses
 * which metrics (requests/volume/bandwidth) and which views (domain×day / by-day
 * / by-domain) to include, plus the period (the page's month or a custom range).
 * Generated + streamed server-side.
 */
export function ExportUsageDialog({
  open,
  onOpenChange,
  serviceId,
  monthLabel,
  monthRange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId?: string;
  monthLabel: string;
  monthRange: { from: string; to: string };
}) {
  const { show } = useToast();
  const exportMut = useUsageExport();
  const [period, setPeriod] = useState<PeriodMode>('month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [metrics, setMetrics] = useState(() => allChecked(METRICS));
  const [views, setViews] = useState<Record<ExportView, boolean>>(() => ({ ...DEFAULT_VIEWS }));

  const selMetrics = picked(metrics);
  const selViews = picked(views);
  const customValid = period === 'month' || (!!fromDate && !!toDate && fromDate <= toDate);
  const canExport = selMetrics.length > 0 && selViews.length > 0 && customValid;

  const close = () => onOpenChange(false);

  const submit = async () => {
    const range =
      period === 'month'
        ? monthRange
        : { from: `${fromDate}T00:00:00Z`, to: `${toDate}T23:59:59Z` };
    try {
      await exportMut.mutateAsync({ serviceId, from: range.from, to: range.to, metrics: selMetrics, views: selViews });
      show({ tone: 'success', title: 'Export ready', description: 'Your download has started.' });
      close();
    } catch (e) {
      show({ tone: 'danger', title: 'Export failed', description: errorMessage(e, 'Please try again.') });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Export usage report</DialogTitle>
          <DialogDescription>
            Downloads a multi-sheet Excel workbook — volume in GB, bandwidth in bps.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {/* Period — first segment mirrors whatever month the page has selected,
                which is not necessarily the current month. */}
            <div role="group" aria-labelledby="export-period-label" style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <Label id="export-period-label">Period</Label>
              <SegmentedControl
                aria-label="Period"
                items={[
                  { value: 'month', label: monthLabel },
                  { value: 'custom', label: 'Custom range' },
                ]}
                value={period}
                onValueChange={(v) => {
                  setPeriod(v as PeriodMode);
                  // Prefill from the selected month so the form is never in a dead state.
                  if (v === 'custom' && !fromDate && !toDate) {
                    setFromDate(monthRange.from.slice(0, 10));
                    setToDate(monthRange.to.slice(0, 10));
                  }
                }}
              />
              {period === 'custom' && (
                <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
                  <DateRangePicker
                    value={{ from: fromDate, to: toDate }}
                    onChange={(r) => {
                      setFromDate(r.from);
                      setToDate(r.to);
                    }}
                  />
                  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
                    Days are interpreted in UTC.
                  </span>
                </div>
              )}
            </div>

            {/* Metrics */}
            <div role="group" aria-labelledby="export-metrics-label" style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <Label id="export-metrics-label">Metrics</Label>
              <div style={{ display: 'flex', gap: 'var(--vn-space-4)', flexWrap: 'wrap' }}>
                {METRICS.map((m) => (
                  <label key={m.id} htmlFor={`metric-${m.id}`} className={styles.inlineOption}>
                    <Checkbox id={`metric-${m.id}`} checked={metrics[m.id]} onCheckedChange={(v) => setMetrics((s) => ({ ...s, [m.id]: v === true }))} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Views */}
            <div role="group" aria-labelledby="export-sheets-label" style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              <Label id="export-sheets-label">Include sheets</Label>
              <div className={styles.sheetGrid}>
                {VIEWS.map((v) => (
                  <label
                    key={v.id}
                    htmlFor={`view-${v.id}`}
                    className={styles.option}
                    data-checked={views[v.id] || undefined}
                  >
                    <Checkbox id={`view-${v.id}`} checked={views[v.id]} onCheckedChange={(c) => setViews((s) => ({ ...s, [v.id]: c === true }))} />
                    <span className={styles.sheetText}>
                      {v.label}
                      <span className={styles.sheetDesc}>{v.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {(selMetrics.length === 0 || selViews.length === 0) && (
              <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                Select at least one metric and one sheet to export.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={close}>Cancel</Button>
          <Button disabled={!canExport} loading={exportMut.isPending} onClick={submit}>
            {exportMut.isPending ? 'Preparing…' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
